import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import ProjectCard from "../components/ProjectCard";
import ProjectModal from "../components/ProjectModal";
import Header from "../components/Header";
import Footer from "../components/Footer";

import {
  projects,
  filterCategories,
  getCategoryCount,
} from "../data/projects";

const PortfolioPage = () => {
  const [active, setActive] = useState("All Projects");
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState(null);

  const filterRef = useRef(null);

  useEffect(() => {
    document.title = `Portfolio | ${projects.length}+ Development Projects | RankifyDev`;
  }, []);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    const baseProjects =
      active === "All Projects"
        ? projects
        : projects.filter((project) =>
            project.categories?.includes(active)
          );

    if (!normalizedQuery) {
      return baseProjects;
    }

    return baseProjects.filter((project) => {
      const searchableContent = [
        project.name,
        project.slug,
        project.niche,
        project.domain,
        project.description,
        project.type,
        project.platform,
        ...(project.categories || []),
        ...(project.frontend || []),
        ...(project.backend || []),
        ...(project.otherSkills || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableContent.includes(normalizedQuery);
    });
  }, [active, query]);

  return (
    <div className="App">
      <Header />
      <main className="pt-24">
        {/* Hero */}
        <section className="relative pt-16 pb-14 overflow-hidden">
          <div className="absolute inset-0 brand-gradient-soft" aria-hidden />
          <div className="absolute -top-24 right-10 h-72 w-72 rounded-full brand-gradient blur-3xl opacity-30 blob-drift" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-3.5 py-1 text-xs font-medium text-brand-deep border border-[hsl(var(--brand-violet)/.15)]">
              Portfolio · {projects.length} projects
            </div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-[hsl(var(--brand-navy))] leading-[1.05] max-w-3xl">
              Every project we've <span className="text-gradient-brand">shipped and scaled.</span>
            </h1>
            <p className="mt-4 text-slate-600 max-w-2xl">
              Websites, Figma UI/UX and mobile applications across {filterCategories.length - 1} industries. Filter by category, search a brand, or open any project to visit the live site.
            </p>

            {/* Search */}
            <div className="mt-8 max-w-md relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="portfolio-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search projects, niche or domain…"
                className="w-full rounded-full border border-black/10 bg-white pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
            </div>
          </div>
        </section>

        {/* Filters */}
        {/* Filters */}
<section className="sticky top-20 z-40 border-y border-black/5 bg-white/85 backdrop-blur-xl">
  <div className="relative mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">

    {/* Left Arrow */}
    <button
      type="button"
      onClick={() =>
        filterRef.current?.scrollBy({
          left: -350,
          behavior: "smooth",
        })
      }
      className="absolute left-2 top-1/2 z-30 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-black/10 bg-white shadow-lg hover:bg-lilac"
      aria-label="Previous categories"
    >
      <ChevronLeft className="h-5 w-5 text-[hsl(var(--brand-navy))]" />
    </button>

    {/* Categories */}
    <div
      ref={filterRef}
      className="no-scrollbar mx-12 flex gap-2 overflow-x-auto scroll-smooth"
    >
      {filterCategories.map((c) => {
        const count = getCategoryCount(c);
        const on = c === active;

        return (
          <button
            key={c}
            type="button"
            onClick={() => setActive(c)}
            className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold transition-all ${
              on
                ? "border-transparent bg-[hsl(var(--brand-navy))] text-white shadow-md"
                : "border-black/10 bg-white text-[hsl(var(--brand-navy))] hover:border-brand/40 hover:text-brand"
            }`}
          >
            {c}

            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                on
                  ? "bg-[hsl(var(--brand-amber))] text-[hsl(var(--brand-navy))]"
                  : "bg-lilac text-brand-deep"
              }`}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>

    {/* Right Arrow */}
    <button
      type="button"
      onClick={() =>
        filterRef.current?.scrollBy({
          left: 350,
          behavior: "smooth",
        })
      }
      className="absolute right-2 top-1/2 z-30 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-black/10 bg-white shadow-lg hover:bg-lilac"
      aria-label="Next categories"
    >
      <ChevronRight className="h-5 w-5 text-[hsl(var(--brand-navy))]" />
    </button>

  </div>
</section>

        {/* Grid */}
        <section className="py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="font-[Fraunces] text-2xl sm:text-3xl font-bold text-[hsl(var(--brand-navy))]">
                {active} <span className="text-slate-400 text-lg font-normal">({filtered.length})</span>
              </h2>
            </div>
            {filtered.length === 0 ? (
              <div className="rounded-3xl bg-lilac p-14 text-center text-slate-500">No projects match your search.</div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {filtered.map((p, i) => (
                  <ProjectCard key={p.slug} project={p} onOpen={setModal} index={i} />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ProjectModal project={modal} onClose={() => setModal(null)} />
    </div>
  );
};

export default PortfolioPage;
